import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { Boton, Panel } from '../ui';
import { BrandLockup } from '../components/BrandLockup';
import { firebaseConfigured, mesaIdToken, mesaLogin, mesaLogout, watchMesaUser } from '../lib/firebase';

export type AuthedFetch = (path: string, init?: RequestInit) => Promise<Response>;

const ERRORES_LOGIN: Record<string, string> = {
  'auth/invalid-credential': 'Email o contraseña incorrectos.',
  'auth/invalid-email': 'Ese email no tiene un formato válido.',
  'auth/missing-password': 'Escribí tu contraseña para continuar.',
  'auth/user-not-found': 'Email o contraseña incorrectos.',
  'auth/wrong-password': 'Email o contraseña incorrectos.',
  'auth/user-disabled': 'Esta cuenta está deshabilitada. Escribinos desde la mesa.',
  'auth/too-many-requests': 'Demasiados intentos. Esperá unos minutos y volvé a probar.',
  'auth/network-request-failed': 'No pudimos conectar. Revisá tu conexión e intentá de nuevo.',
};

export function mensajeLogin(e: unknown): string {
  const codigo = (e as { code?: string })?.code ?? '';
  return ERRORES_LOGIN[codigo] ?? 'No pudimos iniciar sesión. Intentá de nuevo en unos segundos.';
}

/** Traduce la respuesta del BFF a algo que una persona pueda accionar. */
export async function mensajeRespuesta(res: Response): Promise<string> {
  const cuerpo = (await res.text()).trim();
  let detalle = cuerpo;
  try {
    const json = JSON.parse(cuerpo) as { error?: string };
    if (typeof json?.error === 'string') detalle = json.error;
  } catch {
    /* el servidor no siempre responde JSON */
  }
  if (res.status === 401) return 'Tu sesión expiró. Volvé a ingresar.';
  if (res.status === 403) {
    return detalle.includes('operator')
      ? 'Necesitás rol operador para esta acción. Pedísela a la mesa.'
      : 'Tu cuenta no tiene permisos sobre la mesa.';
  }
  if (res.status === 503) return 'El backend de la mesa no está configurado todavía.';
  if (res.status >= 500) return 'El servidor tuvo un problema. Reintentá en unos segundos.';
  return detalle || `No pudimos completar la operación (HTTP ${res.status}).`;
}

export function MarcaMesa({ sub }: { sub: string }) {
  return (
    <BrandLockup
      variante="oscuro"
      className="brand-lockup--mesa"
      titulo="Innovación con IA · Mesa"
      subtitulo={`CESSI · ${sub}`}
    />
  );
}

/** Email, rol (si se conoce) y salir, a la derecha de la barra de la mesa. */
export function CuentaMesa({ user, rol }: { user: User; rol?: 'viewer' | 'operator' | null }) {
  return (
    <div className="mesa-cuenta">
      <span className="mesa-cuenta__email" title={user.email ?? ''}>
        {user.email}
      </span>
      {rol && <span className="mesa-rol">{rol === 'operator' ? 'Operador' : 'Lectura'}</span>}
      <Boton variante="fantasma" onClick={() => void mesaLogout()}>
        Salir
      </Boton>
    </div>
  );
}

export type SesionMesa = { user: User; authedFetch: AuthedFetch };

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = await mesaIdToken();
  if (!token) throw new Error('Sin sesión');
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error(await mensajeRespuesta(res));
  return res;
}

/**
 * Login de la mesa (Firebase). Muestra el ingreso hasta que haya sesión y entonces renderiza
 * `children`; al salir se desmonta el tablero, así no quedan datos ni errores para el próximo.
 */
export function PuertaMesa({ producto, children }: { producto: string; children: (s: SesionMesa) => ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [authListo, setAuthListo] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ingresando, setIngresando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () =>
      watchMesaUser((u) => {
        setUser(u);
        setAuthListo(true);
        setError(null);
      }),
    [],
  );

  const fetchConSesion = useCallback<AuthedFetch>((path, init) => authedFetch(path, init), []);

  if (!firebaseConfigured()) {
    return (
      <div className="shell shell--mesa mesa-auth">
        <div className="mesa-auth__inner">
          <MarcaMesa sub={producto} />
          <Panel titulo="Falta configurar el acceso" subtitulo="Solo del lado del cliente web">
            <p style={{ margin: 0 }}>
              Definí las variables <code>VITE_FIREBASE_*</code> en <code>apps/web/.env</code> para
              habilitar el login de la mesa. Las credenciales del BFF, Postgres y SMTP siguen
              viviendo únicamente en el servidor.
            </p>
          </Panel>
        </div>
      </div>
    );
  }

  if (!authListo) {
    return (
      <div className="shell shell--mesa mesa-auth">
        <div className="mesa-auth__inner">
          <MarcaMesa sub="Verificando tu sesión…" />
          <div className="mesa-skel" style={{ height: 180 }} role="status" aria-label="Cargando" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="shell shell--mesa mesa-auth">
        <div className="mesa-auth__inner">
          <MarcaMesa sub="Acceso restringido al equipo de la mesa" />
          <Panel titulo="Ingresar" subtitulo="Usá tu cuenta habilitada en la mesa CESSI">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (ingresando) return;
                setError(null);
                setIngresando(true);
                void mesaLogin(email.trim(), password)
                  .catch((err: unknown) => setError(mensajeLogin(err)))
                  .finally(() => setIngresando(false));
              }}
            >
              <div className="campo">
                <label htmlFor="mesa-email">Email</label>
                <input
                  id="mesa-email"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="campo">
                <label htmlFor="mesa-password">Contraseña</label>
                <input
                  id="mesa-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {error && (
                <div className="mesa-nota mesa-nota--error" role="alert">
                  <p className="mesa-nota__cuerpo error">
                    <strong>No pudimos ingresar</strong>
                    {error}
                  </p>
                </div>
              )}
              <div className="acciones">
                <Boton type="submit" disabled={ingresando}>
                  {ingresando ? 'Ingresando…' : 'Ingresar'}
                </Boton>
              </div>
            </form>
          </Panel>
          <p className="mesa-auth__pie">
            Los resultados individuales no se comparten con terceros (Ley 25.326).
          </p>
        </div>
      </div>
    );
  }

  return <>{children({ user, authedFetch: fetchConSesion })}</>;
}
